import React, { useState, useEffect } from 'react';
import {
  BarChart3, TrendingUp, Users, Shield,
  Activity, Download, ArrowUpRight, ArrowDownRight,
  Building2, DollarSign, AlertTriangle, ClipboardCheck,
  FileText, GraduationCap, Bug, Loader2, Target
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/analytics/owner-dashboard')
      .then(setData)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-24 text-gray-400" data-testid="analytics-error">
        Failed to load analytics data
      </div>
    );
  }

  const severityData = data.safety_events?.by_severity || {};
  const eventStatusData = data.safety_events?.by_status || {};
  const auditStatusData = data.audits?.by_status || {};
  const riskLevelData = data.risks?.by_level || {};
  const piStatusData = data.pi_projects?.by_status || {};
  const usersByRole = data.users?.by_role || {};
  const trainingStatus = data.training?.by_status || {};
  const facilityStats = data.facility_stats || [];

  const totalSeverity = Object.values(severityData).reduce((a, b) => a + b, 0) || 1;
  const totalRisks = Object.values(riskLevelData).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Analytics & Insights</h1>
          <p className="text-gray-500 mt-1">Platform-wide performance metrics and intelligence</p>
        </div>
        <Button variant="outline" className="gap-2" data-testid="export-btn">
          <Download className="w-4 h-4" />
          Export Report
        </Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Facilities', value: data.facilities?.total || 0, sub: `${data.facilities?.active || 0} active`, icon: Building2, color: 'from-[var(--niko-teal)]/10 to-[var(--niko-green)]/10', iconColor: 'text-[var(--niko-teal)]' },
          { label: 'Total Users', value: data.users?.total || 0, sub: `${data.users?.active || 0} active`, icon: Users, color: 'from-blue-50 to-indigo-50', iconColor: 'text-blue-600' },
          { label: 'Monthly Revenue', value: `$${(data.revenue?.monthly || 0).toLocaleString()}`, sub: `$${(data.revenue?.annual_projected || 0).toLocaleString()}/yr projected`, icon: DollarSign, color: 'from-green-50 to-emerald-50', iconColor: 'text-green-600' },
          { label: 'Safety Events', value: data.safety_events?.total || 0, sub: `${eventStatusData.open || 0} open`, icon: AlertTriangle, color: 'from-orange-50 to-red-50', iconColor: 'text-orange-600' },
        ].map((metric) => (
          <div key={metric.label} className="bg-white rounded-2xl p-5 border border-gray-100" data-testid={`kpi-${metric.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center`}>
                <metric.icon className={`w-5 h-5 ${metric.iconColor}`} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}</p>
            <p className="text-sm text-gray-500 mt-1">{metric.label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{metric.sub}</p>
          </div>
        ))}
      </div>

      {/* Middle Row: Safety Events + Risk Matrix + Audits */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Safety Events by Severity */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Safety Events by Severity</h2>
          <div className="space-y-3">
            {[
              { key: 'critical', label: 'Critical', color: 'bg-red-500' },
              { key: 'high', label: 'High', color: 'bg-orange-500' },
              { key: 'medium', label: 'Medium', color: 'bg-yellow-500' },
              { key: 'low', label: 'Low', color: 'bg-blue-400' },
            ].map(({ key, label, color }) => (
              <div key={key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <span className="text-sm text-gray-500">{severityData[key] || 0}</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${((severityData[key] || 0) / totalSeverity) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">Total Events</span>
            <span className="text-lg font-bold text-gray-900" data-testid="total-events-count">{data.safety_events?.total || 0}</span>
          </div>
        </div>

        {/* Risk Levels */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Risk Register Overview</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'critical', label: 'Critical', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
              { key: 'high', label: 'High', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
              { key: 'medium', label: 'Medium', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
              { key: 'low', label: 'Low', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
            ].map(({ key, label, bg, text, border }) => (
              <div key={key} className={`${bg} border ${border} rounded-xl p-3 text-center`}>
                <p className={`text-2xl font-bold ${text}`}>{riskLevelData[key] || 0}</p>
                <p className="text-xs text-gray-600 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">Total Risks</span>
            <span className="text-lg font-bold text-gray-900" data-testid="total-risks-count">{data.risks?.total || 0}</span>
          </div>
        </div>

        {/* Audits & Compliance */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Audits & Compliance</h2>
          <div className="space-y-3">
            {[
              { key: 'completed', label: 'Completed', icon: '✓', color: 'text-green-600 bg-green-50' },
              { key: 'in_progress', label: 'In Progress', icon: '◎', color: 'text-blue-600 bg-blue-50' },
              { key: 'scheduled', label: 'Scheduled', icon: '◷', color: 'text-yellow-600 bg-yellow-50' },
              { key: 'cancelled', label: 'Cancelled', icon: '✕', color: 'text-gray-500 bg-gray-50' },
            ].map(({ key, label, icon, color }) => (
              <div key={key} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${color}`}>{icon}</span>
                  <span className="text-sm text-gray-700">{label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{auditStatusData[key] || 0}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">Total Audits</span>
            <span className="text-lg font-bold text-gray-900" data-testid="total-audits-count">{data.audits?.total || 0}</span>
          </div>
        </div>
      </div>

      {/* Bottom Row: Users + PI Projects + Quick Stats */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Users by Role */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Users by Role</h2>
          <div className="space-y-4">
            {[
              { key: 'OWNER', label: 'Owners', color: 'bg-purple-500', icon: '♛' },
              { key: 'HOSPITAL_ADMIN', label: 'Hospital Admins', color: 'bg-blue-500', icon: '⛨' },
              { key: 'STAFF', label: 'Staff', color: 'bg-gray-400', icon: '◉' },
            ].map(({ key, label, color, icon }) => {
              const count = usersByRole[key] || 0;
              const total = data.users?.total || 1;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-lg ${color} text-white flex items-center justify-center text-sm`}>{icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      <span className="text-sm font-semibold text-gray-900">{count}</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${color} rounded-full`} style={{ width: `${(count / total) * 100}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PI Projects */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Performance Improvement</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'in_progress', label: 'In Progress', bg: 'bg-blue-50', text: 'text-blue-700' },
              { key: 'completed', label: 'Completed', bg: 'bg-green-50', text: 'text-green-700' },
              { key: 'planning', label: 'Planning', bg: 'bg-yellow-50', text: 'text-yellow-700' },
              { key: 'on_hold', label: 'On Hold', bg: 'bg-gray-50', text: 'text-gray-600' },
            ].map(({ key, label, bg, text }) => (
              <div key={key} className={`${bg} rounded-xl p-3 text-center`}>
                <p className={`text-2xl font-bold ${text}`}>{piStatusData[key] || 0}</p>
                <p className="text-xs text-gray-600 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">Total Projects</span>
            <span className="text-lg font-bold text-gray-900">{data.pi_projects?.total || 0}</span>
          </div>
        </div>

        {/* Quick Module Stats */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Platform Modules</h2>
          <div className="space-y-3">
            {[
              { label: 'Infection Records', value: data.infections?.total || 0, icon: Bug, color: 'text-red-500 bg-red-50' },
              { label: 'Training Programs', value: data.training?.total || 0, icon: GraduationCap, color: 'text-indigo-500 bg-indigo-50', sub: `${trainingStatus.active || 0} active` },
              { label: 'Policies', value: data.policies?.total || 0, icon: FileText, color: 'text-teal-600 bg-teal-50' },
              { label: 'Safety Events', value: data.safety_events?.total || 0, icon: Shield, color: 'text-orange-500 bg-orange-50', sub: `${eventStatusData.action_required || 0} need action` },
            ].map(({ label, value, icon: Icon, color, sub }) => (
              <div key={label} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <span className="text-sm text-gray-700">{label}</span>
                    {sub && <p className="text-xs text-gray-400">{sub}</p>}
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Facility Performance Table */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Facility Performance Breakdown</h2>
          <p className="text-sm text-gray-500 mt-1">Detailed metrics for each healthcare facility</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="facility-performance-table">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Facility</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Users</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Beds</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Events</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Audits</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Risks</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Revenue</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Tier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {facilityStats.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-400">No facility data available</td></tr>
              ) : facilityStats.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50" data-testid="facility-stat-row">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--niko-teal)]/10 to-[var(--niko-green)]/10 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-[var(--niko-teal)]" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{f.name}</p>
                        <p className="text-xs text-gray-400">{[f.city, f.state].filter(Boolean).join(', ') || f.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{(f.user_count || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{f.beds || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      f.safety_events > 5 ? 'bg-red-100 text-red-700' : f.safety_events > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {f.safety_events}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{f.audits}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      f.risks > 3 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {f.risks}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-[var(--niko-teal)]">${(f.monthly_rate || 0).toLocaleString()}/mo</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      f.subscription_tier === 'Enterprise' ? 'bg-purple-100 text-purple-700' :
                      f.subscription_tier === 'Professional' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {f.subscription_tier || 'Basic'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
